import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinAsset } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceAssetsPage() {
  const [assets, setAssets] = useState<FinAsset[]>([]);
  const [running, setRunning] = useState(false);
  const { rows: paginatedAssets, tablePaginationProps: assetsPaginationProps } = useClientPaginatedRows(assets);

  const load = () => {
    financeApi
      .listAssets()
      .then(setAssets)
      .catch((e) => showApiError(e, 'Failed to load assets'));
  };

  useEffect(() => {
    load();
  }, []);

  const runDepreciation = async () => {
    setRunning(true);
    try {
      const result = await financeApi.runDepreciation();
      notify.success(`Depreciation run complete (${result.posted ?? 0} journals posted)`);
      load();
    } catch (e) {
      showApiError(e, 'Depreciation run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Fixed Assets"
        description="Active assets, depreciation method, and batch depreciation run"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Assets' }]}
        actions={
          <Button variant="secondary" disabled={running} onClick={() => void runDepreciation()}>
            {running ? 'Running…' : 'Run depreciation'}
          </Button>
        }
      />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Category</th>
              <th className="p-2 text-right">Cost</th>
              <th className="p-2 text-left">Method</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAssets.map((a) => (
              <tr key={a.id} className="border-b">
                <td className="p-2 font-mono">{a.assetCode}</td>
                <td className="p-2">{a.assetName}</td>
                <td className="p-2">{a.categoryCode ?? '—'}</td>
                <td className="p-2 text-right">{Number(a.purchaseCost).toFixed(2)}</td>
                <td className="p-2">{a.depreciationMethod}</td>
                <td className="p-2">{a.statusCode}</td>
              </tr>
            ))}
            {assets.length === 0 && (
              <tr>
                <td colSpan={6} className="p-4 text-slate-500">
                  No active assets — add via Finance setup or API.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination {...assetsPaginationProps} hasRows={assets.length > 0} />
      </div>
    </div>
  );
}
